import {type FC, memo, useCallback, useState} from 'react';

import classNames from 'classnames';

import type {EditionApiModel as Edition} from '@yourcoach/shared/api/program';
import type {PrivateProfile} from '@yourcoach/shared/api/user';

import {useI18n} from '@yourcoach/i18n';

import Button from '@src/components/Button';
import {ModalStickyFooter} from '@src/components/ModalNew';
import Switch from '@src/components/Switch';

import styles from './PrivateDataRequest.module.css';

type PDRequest = Edition['private_data_request'];

const AVAILABLE_FIELDS = [
  'first_name',
  'middle_name',
  'last_name',
  'email',
  'phone',
  'gender',
  'date_of_birth',
  'location',
] as PDRequest;

export type Props = {
  onSet: (privateDataRequest: PDRequest) => void;
  privateDataRequest: PDRequest;
};

const PrivateDataRequest: FC<Props> = ({onSet, privateDataRequest}) => {
  const {t} = useI18n();
  const [pdRequest, setPdRequest] = useState(privateDataRequest.slice());

  const onSetButtonPress = useCallback(() => {
    onSet(pdRequest.slice());
  }, [onSet, pdRequest]);

  const onRequestChange = useCallback(
    (key: keyof PrivateProfile, keyIndex: number) => {
      const index = pdRequest.findIndex(item => item === key);

      if (index >= 0) {
        pdRequest.splice(index, 1);

        setPdRequest([...pdRequest]);
      } else {
        const tmp = AVAILABLE_FIELDS.map(item => {
          if (!pdRequest.includes(item)) {
            return;
          }

          return item;
        });

        tmp[keyIndex] = key;

        setPdRequest(tmp.filter(Boolean) as PDRequest);
      }
    },
    [pdRequest],
  );

  return (
    <div
      className={classNames('CRUProgramPrivateDataRequest', styles.Component)}
    >
      <div className={styles.container}>
        {AVAILABLE_FIELDS.map((key, i) => {
          const isSelected = pdRequest.includes(key);

          return (
            <div className={styles.row} key={key}>
              <div className={styles.title}>
                {
                  /**
                   * @i18n_parser
                   *
                   * `shared:private_profile.first_name`
                   * `shared:private_profile.middle_name`
                   * `shared:private_profile.last_name`
                   * `shared:private_profile.email`
                   * `shared:private_profile.phone`
                   * `shared:private_profile.gender`
                   * `shared:private_profile.date_of_birth`
                   * `shared:private_profile.location`
                   */
                  t(`shared:private_profile.${key}`)
                }
              </div>
              <Switch
                isOn={isSelected}
                onChange={() => onRequestChange(key, i)}
              />
            </div>
          );
        })}
      </div>
      <ModalStickyFooter>
        <div className={styles.footer}>
          <Button onClick={onSetButtonPress}>{t('shared:confirm')}</Button>
        </div>
      </ModalStickyFooter>
    </div>
  );
};

export default memo(PrivateDataRequest);
